import React from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { RootState } from '../../../../types/common.types';

import { ACCOUNTABILITY, GENERAL, NEW, PLAN, SUPPORT, TRIBE } from '../../../../utils/constants';
import { getClassNames } from '../../../../utils/util';

import './programme-navbar.scss';

const SECTIONS: string[] = [GENERAL, PLAN, ACCOUNTABILITY, SUPPORT, TRIBE];

type Props = {
  onNavigate?: (value: string) => void;
};

const ProgrammeNavbar = ({ onNavigate }: Props) => {
  const location = useLocation();

  const navigate = useNavigate();

  const { programme, programmeSummaries, parentProgramme } = useSelector(({ programme }: RootState) => programme);

  const { status, programmeExternalReference } = useParams();

  const [searchParams] = useSearchParams();

  const isSectionIncomplete = (text: string) => {
    if ((!programme && !parentProgramme) || !programmeSummaries || text === GENERAL || status === NEW) return false;

    const summary = programmeSummaries.find((summary) => {
      if (programme) {
        return summary.externalReference === programme.externalReference;
      } else if (parentProgramme) {
        return summary.externalReference === parentProgramme.parentProgrammeExternalReference;
      }
    });

    if (!summary) return false;

    const { planComplete, accountabilityComplete, supportComplete, tribeComplete } = summary;

    if (text === PLAN && planComplete) return false;

    if (text === ACCOUNTABILITY && accountabilityComplete) return false;

    if (text === SUPPORT && supportComplete) return false;

    if (text === TRIBE && tribeComplete) return false;

    return true;
  };

  const handleButtonClick = (text: string) => {
    if (status === NEW || location.pathname.includes(text)) return;

    let url = `/programmes/${status}/${programmeExternalReference}/${text}`;
    if (text === GENERAL || text === ACCOUNTABILITY) {
      url = `${url}/members`;
    }

    const hasSubProgrammes = searchParams.get('hasSubProgrammes');
    if (hasSubProgrammes) {
      url = `${url}?hasSubProgrammes=${hasSubProgrammes}`;
    }

    if (onNavigate) {
      onNavigate(url);
    } else {
      navigate(url);
    }
  };

  const renderNavItems = () => {
    return SECTIONS.map((text) => (
      <div
        className={getClassNames('programme-navbar__button', {
          active: location.pathname.includes(text) || (status === NEW && text === GENERAL)
        })}
        onClick={() => handleButtonClick(text)}
        key={text}
        data-testid={text}
      >
        <span>{text.toUpperCase()[0]}</span>
        <span>{text.toUpperCase().slice(1)}</span>
        {isSectionIncomplete(text) && (
          <div className="programme-navbar__icon" data-testid={`${text}-icon`}>
            <p>!</p>
          </div>
        )}
      </div>
    ));
  };

  return <div className="programme-navbar">{renderNavItems()}</div>;
};

export default ProgrammeNavbar;
